import React from 'react';
import { useQCM } from '../../contexts/QCMContext';
import { useTranslation } from 'react-i18next';
import QCMOptions from './QCMOptions';
import QCMSolution from './QCMSolution';
import QCMHelp from './QCMHelp';
import SectionContent from '../lab/SectionContent';
import InfoPanel from '../common/InfoPanel';

const FunnelQCMViewer = () => {
    const { t } = useTranslation();
    const { 
        currentQCM, 
        displayMode, 
        setDisplayMode, 
        currentStep, 
        completedSteps, 
        totalSteps, 
        availableSteps, 
        isLastStep,
        goToNextFunnelStep,
        getNextQCM,
        options,
        elapsedTime,
        maxTime,
        isTimerRunning,
        setIsTimerRunning,
        attemptCount,
        degreeOfCertainty,
        hoveredCertainty
    } = useQCM();

    // Get current step data directly from QCM (now managed by context)
    // In our API shape, step-specific options live under currentQCM.step.qcm.
    // However, when loading a specific step, context may set currentQCM directly to the step payload.
    const currentStepData = (currentQCM && (currentQCM.step || currentQCM)) || null;

    // Helpers to split combined question HTML into Use case vs explicit question
    const extractUseCaseAndQuestion = (rawHtml) => {
        if (!rawHtml || typeof rawHtml !== 'string') {
            return { useCaseHtml: '', questionHtml: '' };
        }
        let idx = -1;
        // Try to locate the question area
        const markers = ['<h2>Question', '<h3>Question', 'Question:'];
        for (const m of markers) {
            idx = rawHtml.indexOf(m);
            if (idx !== -1) break;
        }
        let useCaseHtml = idx !== -1 ? rawHtml.slice(0, idx) : rawHtml;
        let questionHtml = idx !== -1 ? rawHtml.slice(idx) : '';

        // Remove any standalone "Use Case" header from the body since it's now the section title
        useCaseHtml = useCaseHtml
            .replace(/<h2[^>]*>\s*Use\s*Case\s*:?\s*<\/h2>/i, '')
            .replace(/<h3[^>]*>\s*Use\s*Case\s*:?\s*<\/h3>/i, '')
            .replace(/Use\s*Case\s*:?/i, '');

        // Remove the "Question" heading prefix from questionHtml
        questionHtml = questionHtml
            .replace(/<h2[^>]*>\s*Question\s*:?\s*<\/h2>/i, '')
            .replace(/<h3[^>]*>\s*Question\s*:?\s*<\/h3>/i, '')
            .replace(/Question\s*:?/i, '');

        return { useCaseHtml, questionHtml };
    };

    const sourceQuestionHtml = currentQCM?.question || currentStepData?.qcm?.question || currentStepData?.question || '';
    const { useCaseHtml, questionHtml } = extractUseCaseAndQuestion(sourceQuestionHtml);

    // Compute New assumptions text (hide if empty or 'None ...')
    const getNewAssumptionsText = () => {
        const na = currentStepData?.qcm?.['new assumption(s)'] || currentStepData?.['new assumption(s)'];
        if (!na) return '';
        let text = '';
        if (typeof na === 'string') text = na;
        else if (Array.isArray(na) && na.length > 0) text = na.join(' ');
        const normalized = (text || '').trim();
        if (!normalized) return '';
        if (/^none\b/i.test(normalized)) return '';
        return normalized;
    };
    const newAssumptionsText = getNewAssumptionsText();

    // Progress bar component
    const ProgressBar = () => {
        if (!availableSteps || availableSteps.length === 0) return null;
        
        const maxStepNumber = Math.max(...availableSteps);
        
        // Debug log
        console.log('🔍 ProgressBar Debug:', {
            currentStep,
            totalSteps,
            availableSteps,
            completedSteps: Array.from(completedSteps),
            maxStepNumber
        });
        
        return (
            <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold text-blue-300 flex items-center">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                        Funnel Progress
                    </h3>
                    <span className="text-sm text-gray-400">
                        Step {currentStep}{totalSteps > 1 ? ` of ${maxStepNumber}` : ''}
                    </span>
                </div>
                
                {totalSteps > 1 ? (
                    <div className="flex space-x-2">
                        {availableSteps.sort((a, b) => a - b).map((stepNumber, index) => {
                            const isCompleted = completedSteps.has(stepNumber);
                            const isCurrent = stepNumber === currentStep;
                            
                            // Debug each step
                            console.log(`Step ${stepNumber} (index ${index}):`, { isCompleted, isCurrent });
                            
                            return (
                                <div
                                    key={stepNumber}
                                    className={`
                                        flex-1 h-3 rounded-full transition-all duration-500 relative
                                        ${isCurrent 
                                            ? 'bg-gradient-to-r from-blue-500 to-purple-500 animate-pulse shadow-lg' 
                                            : isCompleted 
                                                ? 'bg-green-500 shadow-md' 
                                                : 'bg-gray-700'
                                        }
                                    `}
                                >
                                    <div className="h-full flex items-center justify-center">
                                        {isCompleted && (
                                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        )}
                                        <span className="absolute -bottom-5 text-xs text-gray-400">{stepNumber}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="bg-blue-600 h-3 rounded-full shadow-lg">
                        <div className="h-full flex items-center justify-center">
                            <span className="text-xs text-white font-semibold">Step {currentStep}</span>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // New assumptions section removed as per requirements
    const NewAssumptions = () => null;

    // Simple next step button
    const NextStepButton = () => {
        if (displayMode !== 'solution') return null;

        return (
            <div className="mt-6 pt-6 border-t border-dashed border-blue-400 flex flex-col items-center gap-4">
                <div className="flex gap-3">
                    {!isLastStep && (
                        <button
                            type="button"
                            onClick={() => window.dispatchEvent(new CustomEvent('requestNextFunnelStep'))}
                            className="font-bold py-2 px-4 rounded-full shadow-lg transition-all duration-200 ease-in-out transform hover:scale-105 active:scale-95 bg-blue-600 hover:bg-blue-700 text-white"
                        >
                            Next Step
                        </button>
                    )}
                    {isLastStep && totalSteps > 0 && (
                        <button
                            type="button"
                            onClick={() => window.dispatchEvent(new CustomEvent('requestNextQCM'))}
                            className="font-bold py-2 px-4 rounded-full shadow-lg transition-all duration-200 ease-in-out transform hover:scale-105 active:scale-95 bg-blue-600 hover:bg-blue-700 text-white"
                        >
                            Next QCM
                        </button>
                    )}
                </div>
            </div>
        );
    };

    if (!currentQCM || currentQCM.questionType !== 'funnel-qcm') {
        return (
            <div className="text-center text-gray-400 p-8">
                <p>This component is designed for Funnel QCM type only.</p>
            </div>
        );
    }

    if (totalSteps === 0) {
        return (
            <div className="text-center text-red-400 p-8">
                <p>Error: No steps found in this Funnel QCM.</p>
                <p className="text-sm text-gray-500 mt-2">Invalid or missing data structure.</p>
            </div>
        );
    }

    if (!currentStepData) {
        return (
            <div className="text-center text-red-400 p-8">
                <p>Error: Unable to load data for step {currentStep}.</p>
                <p className="text-sm text-gray-500 mt-2">Available steps: {availableSteps ? availableSteps.join(', ') : 'None'}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Progress bar above Theme section */}
            <ProgressBar />

            {/* Main Content with Right Sidebar */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Left Column: Content */}
                <div className="lg:col-span-3 space-y-6">
                    {/* Theme Section */}
                    <div className="bg-gray-800 rounded-lg p-6 border-l-4 border-blue-500 shadow-lg">
                        <div className="flex items-center mb-4">
                            <div className="mr-3">
                                <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                </svg>
                            </div>
                            <h2 className="text-xl font-bold text-blue-300">Theme</h2>
                        </div>
                        <div className="text-lg leading-relaxed">
                            <SectionContent content={currentQCM?.theme} />
                        </div>
                    </div>

                    {/* Context Section */}
                    <div className="bg-gray-800 rounded-lg p-6 border-l-4 border-green-500 shadow-lg">
                        <div className="flex items-center mb-4">
                            <div className="mr-3">
                                <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <h2 className="text-xl font-bold text-green-300">Context</h2>
                        </div>
                        <div className="text-lg leading-relaxed">
                            <SectionContent content={currentQCM?.context} />
                        </div>
                    </div>

                    {/* Use case Section (renamed from Question, without the question text) */}
                    <div className="bg-gray-800 rounded-lg p-6 border-l-4 border-orange-500 shadow-lg min-h-[200px]">
                        <div className="flex items-center mb-4">
                            <div className="mr-3">
                                <svg className="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                </svg>
                            </div>
                            <h2 className="text-xl font-bold text-orange-300">Use case</h2>
                        </div>
                        <div className="text-lg leading-relaxed">
                            {/* Show the base Use case content */}
                            <SectionContent content={useCaseHtml || currentQCM?.context} />
                            {newAssumptionsText && (
                                <div className="mt-3 flex items-start gap-2 text-lg">
                                    <svg className="w-4 h-4 text-orange-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span className="text-orange-300/90">
                                        <span className="font-semibold mr-1">New assumptions:</span>
                                        <span className="text-gray-200">{newAssumptionsText}</span>
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Question Section (new explicit question section below Use case) */}
                    <div className="bg-gray-800 rounded-lg p-6 border-l-4 border-yellow-500 shadow-lg min-h-[120px]">
                    <div className="flex items-center mb-4">
                        <div className="mr-3">
                            <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-bold text-yellow-300">Question</h2>
                    </div>
                    <div className="text-lg leading-relaxed">
                        {displayMode === 'solution' && currentStepData ? (
                            <QCMSolution stepData={currentStepData} />
                        ) : (
                            <>
                                <SectionContent content={questionHtml || currentQCM?.question || currentStepData?.qcm?.question || currentStepData?.question} />
                                {displayMode === 'qcm' && (
                                    <div className="mt-6">
                                        <QCMOptions stepData={currentStepData} />
                                    </div>
                                )}
                                {displayMode === 'help' && (
                                    <div className="mt-6">
                                        <QCMHelp stepData={currentStepData} />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
                </div>

                {/* Right Column: Widgets */}
                <div className="lg:col-span-1">
                    <InfoPanel 
                        elapsedTime={elapsedTime}
                        maxTime={maxTime}
                        attemptCount={attemptCount}
                        difficulty={currentQCM?.metadata?.difficulty}
                        degreeOfCertainty={degreeOfCertainty}
                        hoveredCertainty={hoveredCertainty}
                        showCertainty={false}
                        isTimerRunning={isTimerRunning}
                        qcmId={currentQCM?.qcm_id}
                    />
                </div>
            </div>

            {/* Navigation Buttons */}
            {options.length > 0 && (
                <div className="mt-6 pt-6 border-t border-dashed border-blue-400 flex flex-col items-center gap-4">
                    <div className="flex flex-wrap justify-center gap-3">
                        {options.map((option) => (
                            <button
                                key={option.id}
                                type="button"
                                onClick={option.action}
                                className="font-bold py-2 px-4 rounded-full shadow-lg transition-all duration-200 ease-in-out transform hover:scale-105 active:scale-95 bg-blue-600 hover:bg-blue-700 text-white"
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Step-dependent sections (Progress bar moved above Question section) */}
            
            <NewAssumptions newAssumptions={
                currentStepData?.qcm?.['new assumption(s)'] || 
                currentStepData?.['new assumption(s)']
            } />
            
            {/* Navigation */}
            <NextStepButton />
        </div>
    );
};

export default FunnelQCMViewer;
